// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import { useLocation } from '@reach/router';
import React from 'react';
import { graphql } from 'gatsby';
import { useDniProvider } from '../../dni-provider';
import useUserIdentifier from '../../../hooks/use-user-identifier';
import useLocalStorageState from '../../../hooks/use-local-storage-state';
import { useTemplateProvider } from '../../template-provider';
import { PageTemplate } from '../../../utils/match-page-template';
import { FranchiseData } from '../../../sourcing/source-nodes/sources/franchises';
import { WikipediaGeoLocationData } from '../../../sourcing/source-nodes/sources/geo-wikipedias';
import { Menu } from '../../../components/header/types';
import FranchiseLdJson from './franchise-ld-json';

const LOCAL_STORAGE_IS_MICROSITE_KEY = 'is_microsite';
const LOCAL_STORAGE_USER_ID_KEY = 'sp_visitor_id';

// TODO: Finish typing this

type UniqueContentItem = {
    approvedDate: string,
    content: string,
    rawContent: string,
    pageName: string
};

export interface BaseMicrositeData {
    franchise: (FranchiseData & {
        menu: Menu | null,
        footer: Queries.CsFranchiseFooterNav | null,
        allowedInternalLinks: (string | null)[] | null,
        bannedInternalLinks: (string | null)[] | null,
        servicesProvidedReadable: Queries.FranchiseMicrositeDataFragment['servicesProvidedReadable'],
        servicesProvidedFilterable: Queries.FranchiseMicrositeDataFragment['servicesProvidedFilterable'],
    }) | null,
}

interface MicrositeContextReturnable extends BaseMicrositeData {
    isMicrosite: boolean,
    isLocalCityPage: boolean,
    isMicrositeHomepage: boolean;

    isHyperLocalTemplate: boolean
    isMetroTemplate: boolean
    isMetroStateTemplate: boolean

    name: string|null
    permalink: string | null,

    menu: Menu | null,
    footer: Queries.CsFranchiseFooterNav | null,
    allowedInternalLinks: (string | null)[] | null,
    bannedInternalLinks: (string | null)[] | null,
    targetCity: {
        city?: string | null
        state?: string | null
        content?: string | null
    } | null
    filteredServices: Queries.FranchiseMicrositeDataFragment['filteredServices'],

    uniqueContent: Record<string, UniqueContentItem>
}

const MicrositeContext = React.createContext(null as unknown as MicrositeContextReturnable);

interface MicrositeProps {
    children: React.ReactNode
    data?: {
        targetCity?: Queries.TargetCity
        franchise?: Queries.Franchise
        cmsMetroPage?: Queries.CsMetroPage
        allFranchiseUniqueContent?: Pick<Queries.FranchiseUniqueContentConnection, 'nodes'>
    }
}

const addToDataLayer = (eventName: string, key: string, value: string | number | boolean | null): void => {
    // value must not be null or undefined but maybe be a boolean and be false
    if (value === null || value === undefined) {
        return;
    }
    // dataLAyer should already be initialized as both gatsby-browser and GTM conditionally initializes it
    if ('dataLayer' in window && Array.isArray(window.dataLayer)) {
        // gets the most recent item of the same event type
        const ofEventType = window.dataLayer.filter(item => item.event === eventName);
        const lastOfEventType = ofEventType[ofEventType.length - 1];

        // Update only if either (a) we don't already have that event, or (b) we do, but its value for 'key' is different from 'value'
        if (!lastOfEventType || lastOfEventType[key] !== value) {
            window.dataLayer.push({
                event: eventName,
                [key]: value,
            });
        }
    }
};

export type ExtendedFranchiseData = FranchiseData & {
    wikipediaBasedGeographicAreas?: {
        places?: WikipediaGeoLocationData[]
    },
    allowedInternalLinks?: (string | null)[] | null,
    filteredServices: Queries.FranchiseMicrositeDataFragment['filteredServices'],
    bannedInternalLinks?: (string | null)[] | null,
    menu?: Menu | null,
    footer?: Queries.CsFranchiseFooterNav | null
};

const MicrositeProvider = ({ children, data }: MicrositeProps): JSX.Element => {
    const location = useLocation();
    const pathName: string = location.pathname;

    const { numberMap } = useDniProvider();

    const franchise = React.useMemo(() => {
        if (!data) return null;
        const sourceFranchise = data.franchise || data.cmsMetroPage?.franchises?.[0];
        if (!sourceFranchise) {
            return null;
        }
        const franchiseData = {
            ...sourceFranchise,
            servicesProvidedReadable: sourceFranchise.servicesProvidedReadable?.map(item => ({
                serviceLine: item?.serviceLine || '',
                sort: item?.sort || 0,
            })) || [],
            servicesProvidedFilterable: sourceFranchise.servicesProvidedFilterable?.map(item => ({
                serviceLine: item?.serviceLine || '',
                sort: item?.sort || 0,
            })) || [],
        };
        return franchiseData as unknown as ExtendedFranchiseData;
    }, [data]);

    const {
        name,
        allowedInternalLinks,
        bannedInternalLinks,
        filteredServices,
        menu,
        footer,
        mainPhone,
    } = franchise || {};

    const uniqueContent: Record<string, UniqueContentItem> = (data?.allFranchiseUniqueContent?.nodes || []).reduce((acc, val) => {
        if (val && val.pageName && val.content && val.rawContent) {
            if (acc[val.pageName]) {
                // If new approved date is greater than existing item - returns +1 or -1
                if (val.approvedDate && acc[val.pageName].approvedDate
                    && val.approvedDate.localeCompare(acc[val.pageName].approvedDate) === 1) {
                    acc[val.pageName] = val as unknown as UniqueContentItem;
                }
            } else {
                acc[val.pageName] = val as unknown as UniqueContentItem;
            }
        }
        return acc;
    }, {} as Record<string, UniqueContentItem>);

    const { template: matchedTemplate } = useTemplateProvider();
    if (franchise && mainPhone && 'mainPhone' in franchise && numberMap && mainPhone in numberMap) {
        franchise.mainPhone = numberMap[mainPhone];
    }

    const isMicrosite = matchedTemplate === PageTemplate.MicrositePage || matchedTemplate === PageTemplate.MicrositeService;

    const isHyperLocalTemplate = matchedTemplate === PageTemplate.HyperLocal;
    const isMetroTemplate = matchedTemplate === PageTemplate.Metro;
    const isMetroStateTemplate = matchedTemplate === PageTemplate.MetroState;

    const {
        data: isMicrositeInLocalStorage,
        setData: setIsMicrositeInLocalStorage,
        getData: getSavedIsMicrositeIdBeforeFinishedLoading,
    } = useLocalStorageState<boolean>(
        LOCAL_STORAGE_IS_MICROSITE_KEY,
        async () => isMicrosite,
        [],
        null,
        () => true,
    );

    const isLocalCityPage = !!data?.targetCity;
    const permalink = franchise?.permalink || '';

    const targetCity = (() => {
        const source = data?.targetCity
        || data?.franchise?.serviceCities?.[0];

        return {
            city: source?.city || franchise?.address?.city,
            state: source?.state || franchise?.address?.state,
            content: source?.content || '',
        };
    })();

    const providerValue = React.useMemo(() => {
        const fD: MicrositeContextReturnable = {
            name: name || null,
            allowedInternalLinks: allowedInternalLinks || null,
            bannedInternalLinks: bannedInternalLinks || null,
            filteredServices: filteredServices || [],
            footer: footer || null,
            franchise: franchise as BaseMicrositeData['franchise'],
            isHyperLocalTemplate,
            isLocalCityPage,
            isMetroStateTemplate,
            isMetroTemplate,
            isMicrosite,
            isMicrositeHomepage: Boolean(isMicrosite && permalink && pathName === permalink),
            menu: menu || null,
            permalink: permalink || null,
            targetCity,
            uniqueContent,
        };
        return fD;
    }, [
        name,
        franchise,
        permalink,
        menu,
        footer,
        allowedInternalLinks,
        bannedInternalLinks,
        filteredServices,
        isMicrosite,
        isLocalCityPage,
        targetCity,
        isHyperLocalTemplate,
        isMetroTemplate,
        isMetroStateTemplate,
        uniqueContent,
        pathName,
    ]);

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('Franchise ID', franchise?.franchiseNumber);
        } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line no-console
            window.fID = () => console.log('Franchise ID', franchise?.franchiseNumber);
        }
        // Ignore the ESLint, we only want to log this once for devs
    }, [franchise]);

    const userId = useUserIdentifier();
    const {
        data: userIdInLocalStorage,
        setData: setUserIdInLocalStorage,
        getData: getSavedUserIdBeforeFinishedLoading,
    } = useLocalStorageState<number | null>(
        LOCAL_STORAGE_USER_ID_KEY,
        async () => userId,
        [],
        null,
        () => true,
    );

    React.useEffect(() => {
        if (isMicrosite && userId && franchise?.franchiseNumber) {
            fetch(`/api/microsite?id=${userId}&franchiseNumber=${franchise.franchiseNumber}`)
                .then(r => r.text())
                .catch(() => {});
        }

        // updates isMicrosite in localstorage if needed
        const isMicrositeFromLocalStorage = isMicrositeInLocalStorage || getSavedIsMicrositeIdBeforeFinishedLoading();
        if (isMicrositeFromLocalStorage !== isMicrosite) {
            setIsMicrositeInLocalStorage(isMicrosite);
        }

        // updates userId in localstorage if needed
        const userIdFromLocalstorage = userIdInLocalStorage || getSavedUserIdBeforeFinishedLoading();
        if (userId && userId !== userIdFromLocalstorage) {
            setUserIdInLocalStorage(userId);
        }

        // adds franchiseNumber and userId to the dataLayer
        addToDataLayer('SiteVisitorId', 'SiteVisitorId', userId);
        addToDataLayer('SiteFranchiseNumber', 'FranchiseNumber', franchise?.franchiseNumber || 3000);
    }, [userId, isMicrosite, franchise?.franchiseNumber, isMicrositeInLocalStorage, getSavedIsMicrositeIdBeforeFinishedLoading, userIdInLocalStorage, getSavedUserIdBeforeFinishedLoading, setIsMicrositeInLocalStorage, setUserIdInLocalStorage]);

    return (
        <MicrositeContext.Provider value={providerValue}>
            {franchise && <FranchiseLdJson {...franchise} />}
            {children}
        </MicrositeContext.Provider>
    );
};

const useMicrosite = (): MicrositeContextReturnable => {
    const ctx = React.useContext(MicrositeContext);
    if (!ctx) {
        throw new Error('useMicrosite used outside of context');
    }
    return ctx;
};

// TODO: Fix these exports, MicrositeProvider should be default, don't export MicrositeContext

export { MicrositeContext, MicrositeProvider, useMicrosite };

export const query = graphql`
    fragment FranchiseMicrositeData on Franchise {
        wikipediaBasedGeographicAreas
        legacySiteEnabled
        tempOutOfService
        googleAdsTag
        activeMarkets {
            city
            state
            priority
            link
        }
        areaServedLocationData {
            city
            state
            latitude
            longitude
        }
        relevantAnnouncements {
            uid
            title
            copy
            enable
            callToAction {
                title
                href
            }
        }
        crewPhotos {
            url
            caption
            rawCaption
            name
        }
        bannedInternalLinks
        allowedInternalLinks
        averageRating
        reviewCount
        permalink
        franchiseNumber
        profileExcerpt
        filteredServices {
            serviceType {
                title
                sort
                franchiseServiceMapping
                franchiseLink {
                    href
                    title
                }
                link {
                    href
                    title
                }
                icon {
                    title
                    url
                }
                publishDetails {
                    locale
                }
            }
            franchiseLink
        }
        footer {
            links {
                title
                url
                className
                links {
                    title
                    url
                    className
                }
            }
        }
        menu {
            navItems {
                navItemLink {
                    href
                    title
                }
                columns {
                    group {
                        categoryLink {
                            href
                            title
                        }
                        icon {
                            title
                            url
                        }
                        iconInverse {
                            title
                            url
                        }
                        subgroup {
                            title
                            subgroupLink {
                                href
                                title
                            }
                            links {
                                href
                                title
                            }
                        }
                    }
                }
            }
        }

        socialMedia {
            facebook
            twitter
            instagram
            linkedIn
        }
        paymentMethods {
            americanExpress
            discover
            masterCard
            visa
        }
        profileExcerpt
        aboutUs {
            useCareerPlug
            careerPlugUrl
            ownerPicture
            displayOwners
            multipleOwners
            owners
            affiliations {
                description
            }
            awards {
                description
            }
            certifications {
                description
            }
        }
        licenses {
            generalContractorLicense
            residentialContractorLicense
            commercialContractorLicense
            plumbingContractorLicense
            electricalContractorLicense
            cslbContractorLicense
            moldRemediationContractorLicense
            biohazardContractorLicense
        }
        snapaStatus
        rapaStatus
        capaStatus
        averageRating
        reviewCount
        googleReviewLink
        servicesProvidedReadable {
            serviceLine
            sort
        }
        servicesProvidedFilterable {
            serviceLine
            sort
        }
        name
        address {
            latitude
            longitude
            city
            countryCode
            address
            addressExt
            postalCode
            state
        }
        googlePlaceId
        mainPhone
        addressHidden
    }

    fragment AllFranchiseTestimonialData on FranchiseTestimonial {
        name
        date
        state
        city
        category
        review
        franchiseNumber
        isHomepageWidget
    }
`;
